// Form validation helpers for authentication

/**
 * Email validation using regex pattern
 * @param {string} email - Email address to validate
 * @returns {boolean} - True if valid email format
 */
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Password strength validation
 * Requirements: min 8 characters, uppercase, lowercase, number, special character
 * @param {string} password - Password to validate
 * @returns {object} - { isValid: boolean, errors: string[] }
 */
export const validatePassword = (password) => {
  const errors = [];
  
  if (password.length < 8) {
    errors.push('Password must be at least 8 characters long');
  }
  
  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }
  
  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }
  
  if (!/[0-9]/.test(password)) {
    errors.push('Password must contain at least one number');
  }
  
  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
    errors.push('Password must contain at least one special character');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Calculate password strength (0-100)
 * @param {string} password - Password to check
 * @returns {number} - Strength score (0-100)
 */
export const getPasswordStrength = (password) => {
  let strength = 0;
  
  if (password.length >= 8) strength += 20;
  if (password.length >= 12) strength += 10;
  if (password.length >= 16) strength += 10;
  
  if (/[a-z]/.test(password)) strength += 15;
  if (/[A-Z]/.test(password)) strength += 15;
  if (/[0-9]/.test(password)) strength += 15;
  if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) strength += 15;
  
  return Math.min(strength, 100);
};

/* ------------------------------------------------------------------ *
 * Typed-input filters: a phone box takes only a phone number, a ZIP box
 * only a ZIP, an email box no spaces. Run them in onChange so the field can
 * never hold anything else, and validate on submit for completeness.
 * Every site form uses these (lead modals, signup, profile, experts, home).
 * ------------------------------------------------------------------ */

const digitsOf = (value) => String(value ?? '').replace(/\D/g, '');

/** A US number's ten digits, dropping a leading country code 1. */
const phoneDigits = (value) => {
  const digits = digitsOf(value);
  return (digits.length > 10 && digits.startsWith('1') ? digits.slice(1) : digits).slice(0, 10);
};

/** As-you-type phone formatting: digits only, capped at ten, shown as (555) 555-5555. */
export const formatPhoneInput = (value) => {
  const d = phoneDigits(value);
  if (d.length === 0) return '';
  if (d.length < 4) return `(${d}`;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

/** As-you-type ZIP: digits only, five of them. */
export const formatZipInput = (value) => digitsOf(value).slice(0, 5);

/** As-you-type email: no whitespace anywhere. */
export const formatEmailInput = (value) => String(value ?? '').replace(/\s/g, '');

/** A complete five-digit ZIP. */
export const validateZip = (zip) => /^\d{5}$/.test(String(zip ?? '').trim());

/**
 * Validate phone number: a full ten-digit US number (formatting ignored).
 * @param {string} phone - Phone number to validate
 * @returns {boolean} - True if valid format, or empty (optional field)
 */
export const validatePhone = (phone) => {
  if (!phone) return true; // Optional field
  return phoneDigits(phone).length === 10 && digitsOf(phone).length <= 11;
};

/**
 * Validate date of birth (must be in past and user must be at least 13)
 * @param {string} dob - Date of birth in YYYY-MM-DD format
 * @returns {boolean} - True if valid
 */
export const validateDateOfBirth = (dob) => {
  if (!dob) return true; // Optional field
  
  const date = new Date(dob);
  const now = new Date();
  const age = now.getFullYear() - date.getFullYear();
  
  return date < now && age >= 13 && age < 120;
};

/**
 * Validate required field
 * @param {string} value - Field value
 * @returns {boolean} - True if not empty
 */
export const validateRequired = (value) => {
  return value && value.trim().length > 0;
};
